import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewForm = ({ jobId, partnerName, onSuccess, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast({
        title: "Gabim",
        description: "Ju lutemi zgjidhni një vlerësim (yje).",
        variant: "destructive",
      });
      return;
    }

    if (!customerName || !customerEmail) {
      toast({
        title: "Gabim", 
        description: "Ju lutemi plotësoni emrin dhe email-in.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const reviewData = {
        rating,
        review_text: reviewText,
        customer_name: customerName,
        customer_email: customerEmail
      };

      await axios.post(`${API}/jobs/${jobId}/review`, reviewData);

      toast({
        title: "Faleminderit!",
        description: "Vlerësimi juaj u regjistrua me sukses.",
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Review submission error:', error);
      toast({
        title: "Gabim",
        description: error.response?.data?.detail || "Dështoi regjistrimi i vlerësimit. Ju lutemi provoni përsëri.",
        variant: "destructive",
      });
    }

    setIsSubmitting(false);
  };

  const renderStars = () => {
    return [...Array(5)].map((_, index) => {
      const starValue = index + 1;
      return (
        <button
          key={starValue}
          type="button"
          className={`text-3xl focus:outline-none transition-colors ${
            starValue <= (hoveredRating || rating)
              ? 'text-yellow-400'
              : 'text-gray-300'
          }`}
          onClick={() => setRating(starValue)}
          onMouseEnter={() => setHoveredRating(starValue)}
          onMouseLeave={() => setHoveredRating(0)}
        >
          <Star className="h-8 w-8 fill-current" />
        </button>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="text-center bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
              <CardTitle className="text-2xl font-bold">Vlerëso Shërbimin</CardTitle>
              <p className="text-emerald-100 mt-2">
                Si ishte përvojë juaj me partnerin tonë {partnerName}?
              </p>
            </CardHeader>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Rating Stars */}
                <div className="text-center">
                  <Label className="text-lg font-semibold text-gray-900 block mb-4">
                    Vlerësimi juaj *
                  </Label>
                  <div className="flex justify-center space-x-2 mb-2">
                    {renderStars()}
                  </div>
                  <p className="text-sm text-gray-600">
                    {rating === 0 && "Klikoni në yje për të vlerësuar"}
                    {rating === 1 && "1 yll - Shumë dobët"}
                    {rating === 2 && "2 yje - Dobët"}
                    {rating === 3 && "3 yje - Mesatar"}
                    {rating === 4 && "4 yje - Mirë"}
                    {rating === 5 && "5 yje - Shkëlqyeshëm"}
                  </p>
                </div>

                {/* Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customerName">Emri juaj *</Label>
                    <input
                      id="customerName"
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:border-emerald-500 focus:ring-emerald-500"
                      placeholder="Emri dhe mbiemri"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="customerEmail">Adresa elektronike *</Label>
                    <input
                      id="customerEmail"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:border-emerald-500 focus:ring-emerald-500"
                      placeholder="shembull@email.com"
                      required
                    />
                  </div>
                </div>

                {/* Review Text */}
                <div>
                  <Label htmlFor="reviewText">Koment (opsional)</Label>
                  <Textarea
                    id="reviewText"
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Tregoni më shumë për përvojën tuaj..."
                    rows={4}
                    className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting || rating === 0}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3"
                  >
                    {isSubmitting ? 'Duke dërguar...' : 'Dërgo Vlerësimin'}
                  </Button>
                  
                  {onCancel && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onCancel}
                      className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Anulo
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReviewForm;